// Contexto de autenticação integrado com Supabase
import React, { createContext, useContext, ReactNode, useState, useEffect, useRef } from 'react';
import { supabase } from '../config/supabase';
import { telemetry } from '../services/telemetryService';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import { useTabFocus } from '../utils/tabFocusOptimizer';

// Extensão do tipo User do Supabase para incluir uid
export interface ExtendedUser extends User {
  uid: string;
}

// Tipos para o usuário Supabase
export interface SupabaseUserProfile {
  id?: string;
  uid: string;
  email: string;
  display_name?: string;
  photo_url?: string;
  full_name?: string;
  phone_number?: string;
  role: 'user' | 'admin' | 'premium';
  verified: boolean;
  // Flag de perfil para desbloqueio do Remaker
  remaker_unlocked?: boolean;
  remaker_acquired?: string;
  permissions: any;
  tokens: {
    balance: number;
    totalPurchased: number;
    totalSpent: number;
  };
  usage: {
    downloads: number;
    storageUsed: number;
  };
  subscription: {
    plan: 'free' | 'premium' | 'pro';
    status: 'active' | 'inactive' | 'cancelled';
    expiresAt?: string;
  };
  preferences: {
    theme: 'cyberpunk';
    language: 'pt' | 'en';
    notifications: boolean;
  };
  created_at: string;
  updated_at?: string;
  last_login_at?: string;
}

export interface AuthContextType {
  user: ExtendedUser | null;
  userProfile: SupabaseUserProfile | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  
  // Métodos de autenticação
  signInWithGoogle: () => Promise<void>;
  signInWithGooglePopup: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, displayName?: string) => Promise<void>;
  signInAnonymously: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  
  // Métodos de perfil
  updateUserProfile: (updates: Partial<SupabaseUserProfile>) => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  
  // Métodos de tokens
  getTokenBalance: () => Promise<number>;
  addTokens: (amount: number, reason: string, metadata?: any) => Promise<void>;
  subtractTokens: (amount: number, reason: string, metadata?: any) => Promise<boolean>;
  
  // Utilidades
  clearError: () => void;
}

// Criar o contexto
const SupabaseAuthContext = createContext<AuthContextType | null>(null);

// Hook para usar o contexto
export const useAuth = () => {
  const context = useContext(SupabaseAuthContext);
  if (context) return context;
  return {
    user: null,
    userProfile: null,
    session: null,
    loading: true,
    error: null,
    isAuthenticated: false,
    signInWithGoogle: async () => {},
    signInWithGooglePopup: async () => {},
    signInWithEmail: async () => {},
    signUpWithEmail: async () => {},
    signInAnonymously: async () => {},
    signOut: async () => {},
    resetPassword: async () => {},
    updateUserProfile: async () => {},
    refreshUserProfile: async () => {},
    getTokenBalance: async () => 0,
    addTokens: async () => {},
    subtractTokens: async () => false,
    clearError: () => {}
  } as AuthContextType;
};

// Provedor do contexto
interface AuthProviderProps {
  children: ReactNode;
}

export const SupabaseAuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [userProfile, setUserProfile] = useState<SupabaseUserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Ref para deduping de requests de perfil
  const loadProfilePromiseRef = React.useRef<{ [key: string]: Promise<void> }>({});

  const isAuthenticated = !!user && !!session;

  // Hook para detectar mudanças de foco da aba - SEM callback para evitar loops
  const { isTabActive } = useTabFocus();

  // Ref para acompanhar estado atual dentro do callback (evitar closure stale)
  const sessionRef = useRef<Session | null>(null);
  const userRef = useRef<ExtendedUser | null>(null);

  // Inicialização
  useEffect(() => {
    // Configurar listener para mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      handleAuthStateChange(event, newSession);
    });

    // Listener Global para BroadcastChannel (Sincronização entre abas/janelas)
    const channel = new BroadcastChannel('auth_channel');
    channel.onmessage = async (event) => {
      if (event.data?.type === 'AUTH_CALLBACK_SUCCESS') {
        console.log('🔄 AuthContext: Recebido sinal de login via BroadcastChannel. Atualizando sessão...');
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          await handleAuthStateChange('SIGNED_IN', data.session);
          // Forçar recarregamento do perfil
          if (data.session.user) {
             await loadUserProfile(data.session.user.id, data.session.user);
          }
        }
      }
    };

    // Listener para eventos de Storage (Fallback final para comunicação entre abas)
    const handleStorageChange = async (event: StorageEvent) => {
      if (event.key === 'overpixel_auth_success_trigger') {
         console.log('🔄 AuthContext: Recebido sinal de login via Storage Event. Atualizando sessão...');
         const { data } = await supabase.auth.getSession();
         if (data.session) {
           await handleAuthStateChange('SIGNED_IN', data.session);
           if (data.session.user) {
              await loadUserProfile(data.session.user.id, data.session.user);
           }
         }
      }
    };
    window.addEventListener('storage', handleStorageChange);

    initializeAuth();

    // Safety timeout: ensure loading is set to false after 5 seconds to prevent infinite loading
    const timeoutId = setTimeout(() => {
      setLoading((prevLoading) => {
        if (prevLoading) {
          console.warn('Auth initialization timed out, forcing loading to false');
          return false;
        }
        return prevLoading;
      });
    }, 5000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeoutId);
      channel.close();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Atualizar refs quando state muda
  useEffect(() => {
    sessionRef.current = session;
    userRef.current = user;
  }, [session, user]);

  // Verificar e aplicar código de referência pendente ao logar
  useEffect(() => {
    const checkReferral = async () => {
      if (isAuthenticated && user?.id) {
        const pendingReferralCode = typeof window !== 'undefined' ? window.localStorage.getItem('overpixel_referral_code') : null;
        
        if (pendingReferralCode) {
          console.log('🎁 [Auth] Encontrado código de referência pendente:', pendingReferralCode);
          
          try {
            // Verificar se o usuário já tem um referrer antes de tentar aplicar (para economizar RPC)
            const { data: userData } = await supabase
              .from('users')
              .select('referred_by')
              .eq('uid', user.id)
              .single();
              
            // Só tenta aplicar se não tiver referrer
            if (userData && !userData.referred_by) {
              const { data, error } = await supabase.rpc('apply_referral_code', {
                p_code: pendingReferralCode
              });
              
              if (!error && data?.success) {
                console.log('✅ [Auth] Código de referência aplicado com sucesso!');
                // Recarregar perfil para atualizar status na UI
                refreshUserProfile();
              } else {
                console.warn('⚠️ [Auth] Falha ao aplicar código:', data?.message || error?.message);
              }
            } else {
              console.log('ℹ️ [Auth] Usuário já possui indicação, ignorando código pendente.');
            }
          } catch (err) {
            console.error('❌ [Auth] Erro ao processar código de referência:', err);
          } finally {
            // Sempre limpar o código para não tentar novamente
            window.localStorage.removeItem('overpixel_referral_code');
          }
        }
      }
    };
    
    checkReferral();
  }, [isAuthenticated, user?.id]);

  // Handler separado para mudanças de estado
  const handleAuthStateChange = async (event: string, newSession: Session | null) => {
    // Acessar valores atuais via ref
    const currentSession = sessionRef.current;
    const currentUser = userRef.current;

    // Ignorar eventos de token refresh que não alteram o estado
    if (event === 'TOKEN_REFRESHED') {
      const currentToken = currentSession?.access_token;
      const newToken = newSession?.access_token;
      
      if (currentToken === newToken) {
        return;
      }
    }
    
    // Processar apenas mudanças significativas
    if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || 
        (event === 'TOKEN_REFRESHED' && newSession?.user)) {
      
      if (event === 'SIGNED_IN') {
        telemetry.track('login_success', { method: newSession?.user?.app_metadata?.provider || 'email' });
        telemetry.identify(); // Identify user and link attribution
      } else if (event === 'SIGNED_OUT') {
        telemetry.track('logout');
      }

      if (newSession?.user) {
        // Só atualizar se realmente mudou
        const userChanged = !currentUser || currentUser.id !== newSession.user.id;
        
        setSession(newSession);
        const extendedUser: ExtendedUser = {
          ...newSession.user,
          uid: newSession.user.id
        };
        setUser(extendedUser);
        
        // Só recarregar perfil se o usuário mudou
        if (userChanged || !userProfile) {
          await loadUserProfile(newSession.user.id, newSession.user);
        }
        
      } else {
        setSession(null);
        setUser(null);
        setUserProfile(null);
      }
      
      // Só definir loading como false se ainda estamos carregando
      if (loading) {
        setLoading(false);
      }
    }
  };

  const initializeAuth = async () => {
    try {
      // Verificar se há tokens na URL (Hash) para login via link mágico/externo
      // DESATIVADO: Deixar que o componente AuthCallback.tsx lide com isso exclusivamente
      // para evitar condições de corrida onde o Contexto limpa o hash antes do componente montar.
      /*
      if (typeof window !== 'undefined' && window.location.hash && window.location.hash.includes('access_token')) {
        try {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');
          
          if (accessToken) {
            console.log('Token detectado na URL, restaurando sessão...');
            const { data, error: setSessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken || '',
            });

            if (!setSessionError && data.session) {
              console.log('Sessão restaurada via URL com sucesso');
              // Limpar o hash da URL para não poluir
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
            } else {
              console.error('Falha ao restaurar sessão via URL:', setSessionError);
            }
          }
        } catch (e) {
          console.error('Erro ao processar hash da URL:', e);
        }
      }
      */

      // Verificar sessão existente
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Erro ao obter sessão:', error);
        setError(error.message);
        setLoading(false);
        setIsInitialized(true);
        return;
      }

      if (session?.user) {
        setSession(session);
        const extendedUser: ExtendedUser = {
          ...session.user,
          uid: session.user.id
        };
        setUser(extendedUser);
        await loadUserProfile(session.user.id, session.user);
      }

      setLoading(false);
      setIsInitialized(true);

    } catch (err: any) {
      console.error('Erro na inicialização da autenticação:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Carregar perfil do usuário com cache para evitar chamadas desnecessárias
  const loadUserProfile = async (userId: string, userObject?: User, forceRefresh = false) => {
    // Deduping: Se já existe uma promessa em andamento para este userId, retorna ela
    if (loadProfilePromiseRef.current[userId]) {
      return loadProfilePromiseRef.current[userId];
    }

    const promise = (async () => {
      try {
        // Evitar recarregar se já temos o perfil do mesmo usuário, a menos que seja forçado
        if (!forceRefresh && userProfile && userProfile.uid === userId) {
          return;
        }
        
        const offline = typeof navigator !== 'undefined' && navigator && navigator.onLine === false
        const hidden = typeof document !== 'undefined' && (document as any)?.hidden === true
        if (offline || hidden) return
        let data: any
        let error: any
      {
        const res = await supabase
          .from('users')
          .select('id,uid,email,display_name,token_balance,role,created_at,updated_at,referral_code,referred_by,user_licenses(*)')
          .eq('uid', userId)
          .single();
        data = res.data
        error = res.error
      }
      if (error && String(error?.code || '').toUpperCase() === 'PGRST002') {
        await new Promise(r => setTimeout(r, 800))
        const res2 = await supabase
          .from('users')
          .select('id,uid,email,display_name,token_balance,role,created_at,updated_at,referral_code,referred_by,user_licenses(*)')
          .eq('uid', userId)
          .single();
        data = res2.data
        error = res2.error
      }

      if (error) {
        if (error.code === 'PGRST116') {
          // Usuário não encontrado, criar perfil
          await createUserProfile(userId);
          return;
        }
        throw error;
      }

      // Buscar dados do auth.users para obter metadados (foto, etc)
      // OTIMIZAÇÃO: Usar userObject fornecido ou do state para evitar chamada de rede
      let userMetadata: any = {};
      let authUser = userObject;

      if (!authUser && user && user.id === userId) {
        authUser = user;
      }

      if (authUser) {
        userMetadata = authUser.user_metadata || {};
      } else {
        // Fallback: só chama getUser se realmente não tivermos o objeto usuário
        const { data: authData } = await supabase.auth.getUser();
        if (authData?.user?.id === userId) {
           userMetadata = authData.user.user_metadata || {};
        }
      }

      // Mapear plano do banco para o formato do frontend
      const mapPlanToFrontend = (planKey: string | null) => {
        if (!planKey) return 'free';
        
        switch (planKey.toLowerCase()) {
          case 'plano básico':
          case 'explorador':
            return 'explorador';
          case 'criador':
          case 'premium':
          case 'pro':
            return 'premium';
          case 'empresarial_pro':
          case 'enterprise':
            return 'pro';
          case 'melhorador-annual':
          case 'dtf-fabart-monthly':
             return planKey; // Retornar a chave original para novos planos
          default:
            return 'free';
        }
      };

      // Determinar status da subscription baseado em user_licenses
      const getSubscriptionFromLicenses = (licenses: any[]) => {
        if (!licenses || licenses.length === 0) {
            return {
                plan: 'free',
                status: 'inactive',
                expiresAt: undefined
            };
        }

        // Encontrar a licença ativa mais recente ou relevante
        // Prioridade para planos pagos e status ativo
        const activeLicense = licenses.find((l: any) => {
             if (l.status !== 'active') return false;
             if (!l.expires_at) return true; // Vitalício
             return new Date(l.expires_at) > new Date();
        });

        if (activeLicense) {
            return {
                plan: mapPlanToFrontend(activeLicense.plan_key),
                status: 'active',
                expiresAt: activeLicense.expires_at
            };
        }

        return {
            plan: 'free',
            status: 'inactive',
            expiresAt: undefined
        };
      };

      const subscriptionData = getSubscriptionFromLicenses((data as any).user_licenses || []);

      // Combinar dados da tabela users com metadados do auth
      const combinedProfile = {
        ...data,
        photo_url: userMetadata?.avatar_url || userMetadata?.picture,
        full_name: data.display_name || userMetadata?.full_name || userMetadata?.name,
        remaker_unlocked: !!(data as any)?.remaker_unlocked,
        remaker_acquired: (data as any)?.remaker_acquired || 'NO',
        tokens: {
          balance: data.token_balance || 0,
          totalPurchased: 0,
          totalSpent: 0
        },
        usage: {
          downloads: 0,
          storageUsed: 0
        },
        subscription: subscriptionData,
        preferences: {
          theme: 'cyberpunk' as const,
          language: 'pt' as const,
          notifications: true
        },
        permissions: {
          customPermissions: [],
          restrictions: []
        },
        verified: true
      };

      setUserProfile(combinedProfile);
    } catch (err: any) {
      console.error('Erro ao carregar perfil do usuário:', err);
      setError(err.message);
    } finally {
      // Limpar a promessa do ref quando terminar
      delete loadProfilePromiseRef.current[userId];
    }
  })();
  
  loadProfilePromiseRef.current[userId] = promise;
  return promise;
  };

  // Criar perfil do usuário
  const createUserProfile = async (userId: string) => {
    try {
      const userData = user;
      if (!userData) return;

      // Inserir apenas os campos que existem na tabela users
      const newProfile = {
        uid: userId,
        email: userData.email || '',
        display_name: userData.user_metadata?.display_name || userData.user_metadata?.full_name || '',
        role: 'user',
        token_balance: 20 // Bônus de boas-vindas
      };

      const { data, error } = await supabase
        .from('users')
        .insert([newProfile])
        .select()
        .single();

      if (error) throw error;

      // Adicionar bônus de boas-vindas ao ledger
      await supabase.rpc('tokens_transfer_atomic_ledger', {
        p_user_uid: userId,
        p_amount: 20,
        p_transaction_type: 'credit',
        p_reason: 'Bônus de boas-vindas'
      });

      // Carregar perfil completo após criação
      await loadUserProfile(userId);
    } catch (err: any) {
      console.error('Erro ao criar perfil do usuário:', err);
      setError(err.message);
    }
  };

  // Login com Google
  // Agora preserva um código de indicação (referral) caso exista em localStorage
  // para que o callback consiga aplicar o benefício ao novo usuário.
  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Limpar flag de popup pendente para evitar conflitos no callback
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('overpixel_auth_popup_pending');
      }
      
      // Verificar se há um código de referral salvo
      const referralCode = (typeof window !== 'undefined')
        ? window.localStorage.getItem('overpixel_referral_code')
        : null;

      // 1. Determinar a origem (domínio) atual dinamicamente
      let origin = typeof window !== 'undefined' ? window.location.origin : 'https://overpixel.web.app';
      
      // Detectar ambiente local dinamicamente para suportar qualquer porta (3000, 5173, 8000, etc)
      // IMPORTANTE: Adicione a URL completa (ex: http://localhost:8000/auth/callback) no painel do Supabase -> Auth -> URL Configuration -> Redirect URLs
      if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
        origin = window.location.origin;
      }

      // Simplificado: Usar apenas a base URL para garantir match exato com a whitelist do Supabase
      // Parâmetros como 'ref', 'intent' e 'return' já estão salvos no localStorage
      const redirectTo = `${origin}/auth/callback`;

      console.log('🚀 [AuthContext] Iniciando OAuth com Redirect URL:', redirectTo);
      if (origin.includes('localhost')) {
        console.warn('⚠️ ATENÇÃO: Se você for redirecionado para a produção, adicione esta URL exata no Painel do Supabase -> Authentication -> URL Configuration -> Redirect URLs:', redirectTo);
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });
      
      if (error) throw error;
    } catch (err: any) {
      console.error('Erro no login com Google:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Login com Google via Popup (Nova Janela)
  const signInWithGooglePopup = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Verificar se há um código de referral salvo
      const referralCode = (typeof window !== 'undefined')
        ? window.localStorage.getItem('overpixel_referral_code')
        : null;

      // 1. Determinar a origem (domínio) atual dinamicamente
      let origin = typeof window !== 'undefined' ? window.location.origin : 'https://overpixel.online';
      
      // Forçar localhost:8000 se estiver rodando localmente (fix para porta incorreta ou problemas de origem)
      if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
        origin = 'http://localhost:8000';
      }

      // USAR ROTA DEDICADA PARA POPUP
      const baseCallback = `${origin}/auth/popup-callback`;
      
      const params = new URLSearchParams();
      if (referralCode) params.set('ref', String(referralCode));
      
      const queryString = params.toString();
      const redirectTo = queryString ? `${baseCallback}?${queryString}` : baseCallback;
      
      console.log('🚀 [AuthContext] Iniciando OAuth Popup com Redirect URL:', redirectTo);

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          skipBrowserRedirect: true,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      if (error) throw error;
      if (!data.url) throw new Error('Não foi possível iniciar o login com Google (URL ausente)');

      // Abrir Popup Centralizado
      const width = 500;
      const height = 600;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      
      // Marcar que estamos iniciando um fluxo de popup
      window.localStorage.setItem('overpixel_auth_popup_pending', 'true');
      console.log('🚀 Iniciando fluxo de login popup. Flag gravada no LocalStorage.');

      const popup = window.open(
        data.url,
        'Google Login',
        `width=${width},height=${height},top=${top},left=${left},status=yes,scrollbars=yes`
      );

      if (!popup) {
         throw new Error('Popup bloqueado pelo navegador. Por favor, permita popups para continuar.');
      }

      // Monitorar mensagens do popup e BroadcastChannel
      return new Promise<void>((resolve, reject) => {
          const channel = new BroadcastChannel('auth_channel');
          let resolved = false;

          const cleanup = () => {
             if (resolved) return;
             resolved = true;
             window.removeEventListener('message', messageHandler);
             channel.close();
             if (checkClosed) clearInterval(checkClosed);
          };

          const handleSuccess = async () => {
             if (resolved) return;
             console.log('Login via Popup concluído com sucesso!');
             
             // Atualizar sessão
             const { data: sessionData } = await supabase.auth.getSession();
             if (sessionData.session) {
                 await handleAuthStateChange('SIGNED_IN', sessionData.session);
                 setLoading(false);
                 cleanup();
                 resolve();
                 // Tentar fechar o popup se ainda estiver aberto (referência pode estar stale, mas tentamos)
                 if (popup && !popup.closed) popup.close();
             } else {
                 cleanup();
                 reject(new Error('Sessão não encontrada após login'));
                 setLoading(false);
             }
          };

          // Handler para BroadcastChannel
          channel.onmessage = (event) => {
             if (event.data?.type === 'AUTH_CALLBACK_SUCCESS') {
                 handleSuccess();
             }
          };

          // Handler para postMessage (fallback)
          const messageHandler = async (event: MessageEvent) => {
              // Validar origem por segurança
              if (event.origin !== window.location.origin) return;
              
              if (event.data?.type === 'AUTH_CALLBACK_SUCCESS') {
                  handleSuccess();
              }
          };

          window.addEventListener('message', messageHandler);

          // Monitorar se o usuário fechou o popup manualmente
          const checkClosed = setInterval(() => {
              // Verificar sinal de sucesso via LocalStorage (Fallback nuclear)
              const successTrigger = window.localStorage.getItem('overpixel_auth_success_trigger');
              if (successTrigger) {
                  const timestamp = parseInt(successTrigger);
                  // Aceitar triggers recentes (últimos 15 segundos)
                  if (!isNaN(timestamp) && Date.now() - timestamp < 15000) {
                      console.log('🔄 AuthContext: Detectado sinal de sucesso via LocalStorage Polling');
                      handleSuccess();
                      clearInterval(checkClosed);
                      return;
                  }
              }

              // Verificar se já temos sessão (Cookie/LocalStorage compartilhado)
              supabase.auth.getSession().then(({ data }) => {
                  if (data.session) {
                      console.log('🔄 AuthContext: Sessão detectada durante polling!');
                      handleSuccess();
                      clearInterval(checkClosed);
                  }
              });

              if (popup.closed) {
                  clearInterval(checkClosed);
                  if (!resolved) {
                      // Verificar se logou mesmo com a janela fechada (race condition)
                      supabase.auth.getSession().then(({ data }) => {
                          if (data.session) {
                              handleSuccess();
                          } else {
                              cleanup();
                              setLoading(false);
                              // Não rejeitar, apenas cancelar o loading, pois usuário pode ter fechado intencionalmente
                          }
                      });
                  }
              }
          }, 1000);
      });

    } catch (err: any) {
      console.error('Erro no login com Google Popup:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Login com email e senha
  const signInWithEmail = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
    } catch (err: any) {
      console.error('Erro no login com email:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Cadastro com email e senha
  const signUpWithEmail = async (email: string, password: string, displayName?: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName,
            full_name: displayName
          }
        }
      });

      if (error) throw error;
    } catch (err: any) {
      console.error('Erro no cadastro:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Login anônimo (compatibilidade)
  const signInAnonymously = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Supabase não tem login anônimo nativo, então criamos um usuário temporário
      // ou redirecionamos para login com Google
      console.warn('Login anônimo não suportado no Supabase. Redirecionando para Google.');
      await signInWithGoogle();
    } catch (err: any) {
      console.error('Erro no login anônimo:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Logout
  const signOut = async () => {
    try {
      setLoading(true);
      // Usar signOut sem scope global para evitar erros de rede
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) {
        console.warn('Erro no logout:', error);
        // Mesmo com erro, limpar o estado local
      }
      
      // Limpar estado local independentemente do resultado
      setUser(null);
      setUserProfile(null);
      setSession(null);
    } catch (err: any) {
      console.error('Erro no logout:', err);
      // Mesmo com erro, limpar o estado local
      setUser(null);
      setUserProfile(null);
      setSession(null);
    } finally {
      setLoading(false);
    }
  };

  // Reset de senha
  const resetPassword = async (email: string) => {
    try {
      setError(null);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`
      });
      if (error) throw error;
    } catch (err: any) {
      console.error('Erro no reset de senha:', err);
      setError(err.message);
    }
  };

  // Atualizar perfil do usuário
  const updateUserProfile = async (updates: Partial<SupabaseUserProfile>) => {
    try {
      if (!user) throw new Error('Usuário não autenticado');
      
      setError(null);
      const { error } = await supabase
        .from('users')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('uid', user.id);

      if (error) throw error;
      
      await refreshUserProfile();
    } catch (err: any) {
      console.error('Erro ao atualizar perfil:', err);
      setError(err.message);
    }
  };

  // Recarregar perfil do usuário
  const refreshUserProfile = async () => {
    if (user) {
      await loadUserProfile(user.id, undefined, true);
    }
  };

  // Obter saldo de tokens
  const getTokenBalance = async (): Promise<number> => {
    try {
      if (!user) return 0;
      // Cache simples com TTL de 30s para reduzir egress
      const cacheKey = `users:token_balance:${user.id}`
      const now = Date.now()
      try {
        const raw = sessionStorage.getItem(cacheKey)
        if (raw) {
          const parsed = JSON.parse(raw)
          if (parsed && parsed.expiresAt && parsed.expiresAt > now) {
            return Number(parsed.value || 0)
          }
        }
      } catch {}

      const { data, error } = await supabase
        .from('users')
        .select('token_balance')
        .eq('uid', user.id)
        .single();

      if (error) throw error;
      const val = Number(data?.token_balance || 0)
      try { sessionStorage.setItem(cacheKey, JSON.stringify({ value: val, expiresAt: now + 300000 })) } catch {}
      return val;
    } catch (err: any) {
      console.error('Erro ao obter saldo de tokens:', err);
      return 0;
    }
  };

  // Adicionar tokens
  const addTokens = async (amount: number, reason: string, metadata?: any) => {
    try {
      if (!user) throw new Error('Usuário não autenticado');
      
      const { error } = await supabase.rpc('tokens_transfer_atomic_ledger', {
        p_user_uid: user.id,
        p_amount: amount,
        p_transaction_type: 'credit',
        p_reason: reason
      });

      if (error) throw error;
      
      await refreshUserProfile();
    } catch (err: any) {
      console.error('Erro ao adicionar tokens:', err);
      setError(err.message);
    }
  };

  // Subtrair tokens
  const subtractTokens = async (amount: number, reason: string, metadata?: any): Promise<boolean> => {
    try {
      if (!user) throw new Error('Usuário não autenticado');
      
      const { data, error } = await supabase.rpc('tokens_transfer_atomic_ledger', {
        p_user_uid: user.id,
        p_amount: -amount,
        p_transaction_type: 'debit',
        p_reason: reason
      });

      if (error) throw error;
      
      await refreshUserProfile();
      return data; // Retorna true se sucesso, false se saldo insuficiente
    } catch (err: any) {
      console.error('Erro ao subtrair tokens:', err);
      setError(err.message);
      return false;
    }
  };

  // Limpar erro
  const clearError = () => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    userProfile,
    session,
    loading,
    error,
    isAuthenticated,
    signInWithGoogle,
    signInWithGooglePopup,
    signInWithEmail,
    signUpWithEmail,
    signInAnonymously,
    signOut,
    resetPassword,
    updateUserProfile,
    refreshUserProfile,
    getTokenBalance,
    addTokens,
    subtractTokens,
    clearError
  };

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  );
};

export { SupabaseAuthContext };
export default SupabaseAuthProvider;

// ===== HOOKS DE CONVENIÊNCIA =====

/**
 * Hook para verificar se o usuário está autenticado
 */
export const useIsAuthenticated = (): boolean => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
};

/**
 * Hook para obter apenas os dados do usuário
 */
export const useUser = () => {
  const { user, userProfile } = useAuth();
  return { user, userProfile };
};

/**
 * Hook para verificar se o usuário é admin
 */
export const useIsAdmin = (): boolean => {
  const { userProfile } = useAuth();
  return userProfile?.role === 'admin';
};

/**
 * Hook para verificar se o usuário tem uma permissão específica
 */
export const useHasPermission = (permission: string): boolean => {
  const { userProfile } = useAuth();
  return userProfile?.permissions?.customPermissions?.includes(permission) ?? false;
};

/**
 * Hook para obter informações de tokens do usuário
 */
export const useTokens = () => {
  const { userProfile, getTokenBalance, addTokens, subtractTokens } = useAuth();
  
  return {
    tokens: userProfile?.tokens?.balance ?? 0,
    totalPurchased: userProfile?.tokens?.totalPurchased ?? 0,
    totalSpent: userProfile?.tokens?.totalSpent ?? 0,
    usage: userProfile?.usage ?? {
      downloads: 0,
      storageUsed: 0
    },
    getTokenBalance,
    addTokens,
    subtractTokens,
    hasTokens: (amount: number) => (userProfile?.tokens?.balance ?? 0) >= amount
  };
};

/**
 * Hook para obter informações de assinatura do usuário
 */
export const useSubscription = () => {
  const { userProfile } = useAuth();
  
  return {
    subscription: userProfile?.subscription ?? {
      plan: 'free' as const,
      status: 'inactive' as const
    },
    isPremium: userProfile?.subscription?.plan !== 'free',
    isActive: userProfile?.subscription?.status === 'active'
  };
};

/**
 * Hook para ações de autenticação
 */
export const useAuthActions = () => {
  const { 
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
    resetPassword,
    updateUserProfile,
    refreshUserProfile,
    clearError
  } = useAuth();
  
  return {
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
    resetPassword,
    updateUserProfile,
    refreshUserProfile,
    clearError
  };
};

/**
 * Hook para estado de carregamento e erros
 */
export const useAuthStatus = () => {
  const { loading, error } = useAuth();
  
  return {
    loading,
    error,
    isLoading: loading,
    hasError: !!error
  };
};
